import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { pxToRem } from '@geneo2-web/shared-ui';
import { TopbarData } from '../mockData';
import { SessionStatCard } from '../../SessionStatCard';
import { ProgressRing } from '../../ProgressRing.tsx';

const styles = {
  card: {
    width: '100%',
  },

  row: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: {
      xs: 'column',
      md: 'row',
    },
    gap: pxToRem(28),
  },

  stacked: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: pxToRem(16),
    width: '100%',
  },

  scoreSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flexShrink: 0,
  },

  ringWrapper: {
    width: pxToRem(180),
    height: pxToRem(180),
    marginBottom: pxToRem(14),
    filter: 'drop-shadow(0 6px 16px rgba(102,126,234,0.18))',
  },

  ringWrapperStacked: {
    width: pxToRem(150),
    height: pxToRem(150),
    marginBottom: pxToRem(8),
    filter: 'drop-shadow(0 6px 16px rgba(34,197,94,0.18))',
  },

  scoreMessage: {
    fontSize: pxToRem(14),
    fontWeight: 600,
    color: '#475569',
    textAlign: 'center',
    maxWidth: pxToRem(320),
  },

  divider: {
    width: {
      xs: '80px',
      md: '1px',
    },
    height: {
      xs: '1px',
      md: pxToRem(80),
    },
    background: '#e2e8f0',
    flexShrink: 0,
  },

  statsGrid: {
    display: 'grid',
    gap: pxToRem(12),
    flex: 1,
    width: '100%',
  },
};

export const TopbarCard = ({ topbar }: { topbar: TopbarData }) => {
  const { t } = useTranslation();
  const isStacked = topbar.layout === 'stacked';
  const showExtendedStats =
    topbar.stats.accuracy != null && topbar.stats.avgPerQuestion != null;
  const timedOutCount = topbar.stats.timedOut;
  const skippedCount = topbar.stats.skipped;
  const hasTimedOut = timedOutCount != null;
  const hasSkipped = skippedCount != null;
  const timedOutOrSkippedValue =
    hasTimedOut && hasSkipped
      ? (timedOutCount ?? 0) + (skippedCount ?? 0)
      : hasTimedOut
      ? timedOutCount ?? 0
      : skippedCount ?? 0;
  const timedOutOrSkippedLabel =
    hasTimedOut && hasSkipped
      ? `${t('PAL_STAT_TIMED_OUT')} / ${t('PAL_STAT_SKIPPED')}`
      : hasTimedOut
      ? t('PAL_STAT_TIMED_OUT')
      : t('PAL_STAT_SKIPPED');
  const timedOutOrSkippedType =
    hasTimedOut && !hasSkipped ? 'timedOut' : hasSkipped && !hasTimedOut ? 'skipped' : 'timedOut';

  const stats = (
    <Box
      sx={{
        ...styles.statsGrid,
        gridTemplateColumns: {
          xs: 'repeat(2, 1fr)',
          md:
            showExtendedStats && !isStacked
              ? 'repeat(3, 1fr)'
              : 'repeat(2, 1fr)',
        },
        alignSelf: { xs: 'stretch', md: isStacked ? 'stretch' : 'unset' },
      }}
    >
      <SessionStatCard
        icon="✓"
        label={t('PAL_STAT_CORRECT')}
        value={topbar.stats.correct}
        type="correct"
      />
      <SessionStatCard
        icon="✗"
        label={t('PAL_STAT_INCORRECT')}
        value={topbar.stats.incorrect}
        type="incorrect"
      />
      <SessionStatCard
        icon="⏭"
        label={timedOutOrSkippedLabel}
        value={timedOutOrSkippedValue}
        type={timedOutOrSkippedType}
      />
      <SessionStatCard
        icon="⏱"
        label={t('PAL_STAT_TIME')}
        value={topbar.stats.time}
        type="time"
        subtitle={topbar.stats.timeSubtitle}
      />
      {showExtendedStats && !isStacked ? (
        <>
          <SessionStatCard
            icon="🎯"
            label={t('PAL_STAT_ACCURACY')}
            value={topbar.stats.accuracy!}
            type="accuracy"
          />
          <SessionStatCard
            icon="⚡"
            label={t('PAL_STAT_AVG_QUESTION')}
            value={topbar.stats.avgPerQuestion!}
            type="avg"
          />
        </>
      ) : null}
    </Box>
  );

  if (isStacked) {
    return (
      <Box sx={styles.card}>
        <Box sx={styles.stacked}>
          <Box sx={styles.scoreSection}>
            <Box sx={styles.ringWrapperStacked}>
              <ProgressRing
                value={topbar.score}
                total={topbar.total}
                content={topbar.content}
                diameter={150}
                color="rgb(34, 197, 94)"
              />
            </Box>
            {topbar.message ? (
              <Typography sx={styles.scoreMessage}>{topbar.message}</Typography>
            ) : null}
          </Box>
          {stats}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={styles.card}>
      <Box sx={styles.row}>
        <Box sx={styles.scoreSection}>
          <Box sx={styles.ringWrapper}>
            <ProgressRing
              value={topbar.score}
              total={topbar.total}
              content={topbar.content}
              diameter={180}
              color="rgb(245, 158, 11)"
            />
          </Box>
          <Typography sx={styles.scoreMessage}>{topbar.message}</Typography>
        </Box>
        <Box sx={styles.divider} />
        {stats}
      </Box>
    </Box>
  );
};
